import React, { useEffect, useState } from "react";
import { useApiClient } from "../../context/ApiClientContext";
import {
  Box,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Grid,
} from "@mui/material";
import api from "../../helper/baseApi";
import { useNavigate } from "react-router-dom";
import CustomRadarChart from "../../components/radarChart";
import CustomBarChart from "../../components/barChart";
import Department from "./department";
import CustomCarousel from "../../components/customCarousal";
import { DataGrid } from "@mui/x-data-grid";
import SimpleLineChart from "../../components/lineChart";
import UserOverviewChart from "./userOverview";
import MissionCreatorChart from "./missionCreator";
import DormantUserTable from "./dormantUserTable";
import CardCounter from "./cardsCounter";
import MissionCreatedBarchart from "./missionCreatedBarchart";
import MissionEarnedChart from "./missionEarnedChart";
import UserActicityStatusTable from "./userActivityStatusTable";
import DailyActivityReportLineChart from "./dailyActivityReportLineChart";
import DateFilter from "./dateFIlter";

export default function InfoDashboard() {
  const { apiUrls: { mobileApiUrl } } = useApiClient();
  const theme = useTheme();

  const [stats, setStats] = useState({
    users: 0,
    engagementRate: 0,
    campaign: 0,
    missions: 0,
    dormant: 0,
  });

  // datasets for charts
  const [createdBarData, setCreatedBarData] = useState([]);
  const [earnedBarData, setEarnedBarData] = useState([]);
  const [avgPercentages, setAvgPercentages] = useState([]);
  const [avgCreated, setAvgCreated] = useState({ metricsWithName: [], metrics: [], values: [] });
  const [avgEarned, setAvgEarned] = useState({ metricsWithName: [], metrics: [], values: [] });
  const [activeUserData, setActiveUserData] = useState([]);
  const [missionCreators, setMissionCreators] = useState([]);
  const [userOverview, setUserOverview] = useState([]);
  const [dormantUser, setDormantUser] = useState([]);
  const [activityList, setActivityList] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const employeeId = localStorage.getItem("employeeId");
  const stored = sessionStorage.getItem("selectedDepartments");
  const deptIds = stored ? JSON.parse(stored) : [];
  const [selectedDeptIds, setSelectedDeptIds] = useState(deptIds);
  const [filter, setFilter] = useState("day");

  const fetchActiveList = async (f) => {
  try {
    const filterToUse = f || filter;
    const apiUrl = `${mobileApiUrl}dashboard/daily-active-graph?filter=${filterToUse}`;
    const response = await api.get(apiUrl);
    const d = response.data?.data?.dailyActiveLists ?? [];

    // ✅ Remove duplicates by date
    const uniqueByDate = Object.values(
      d.reduce((key, current) => {
        key[current.date] = current; // keeps the last one per date
        return key;
      }, {})
    );

    setActivityList(uniqueByDate);
  } catch (error) {
    console.error("Fetch error", error);
  }
};

  const updateFilter = (newFilter) => {
    setFilter(newFilter);
    fetchActiveList(newFilter);
  };

  useEffect(() => {
    fetchActiveList();
  }, []);

  const fetchStats = async () => {
    try {
      const apiUrl = `${mobileApiUrl}dashboard/real-time-analysis`;
      const response = await api.get(apiUrl);
      const d = response.data?.data ?? response.data ?? {};
      
      setStats({
        users: d.userCount ?? 0,
        engagementRate: d.engagementRate ?? 0,
        missions: d.missionCount ?? 0,
        campaign: d.campaignCount ?? 0,
        dormant: d.dormantCount ?? 0,
      });
    } catch (error) {
      // ErrorAlert(error);
    }
  };

  const cards = [
    { label: "POISUM User", value: stats.users, status: 'done' },
    { label: "Engagement Rate", value: `${stats.engagementRate}%`, status: 'done' },
    { label: "Active Mission", value: stats.missions, status: 'done' },
    { label: "Active Campaign", value: stats.campaign, status: 'done' },
    { label: "Dormant User", value: dormantUser.length, status: 'done' },
  ];

  // fetch for mission created & earned, mission creator list, user overview
  const fetchMostData = async (departmentIds, startDate, endDate) => {
    try {
      const apiUrl = `${mobileApiUrl}dashboard/created-earned-analysis`;

      const res = await api.post(apiUrl, {
        employeeId: Number(employeeId),
        departmentIds,
        startDate,
        endDate
      });

      const missionCreated = res.data?.data?.missionCreated ?? [];
      if (missionCreated.length === 0) {
        setCreatedBarData([]);
        setEarnedBarData([]);
        return;
      }

      const created = missionCreated.map((item) => ({

        category: item.valueTagName,
        value: item.missionCount ?? 0,
      }));

      const earned = missionCreated.map((item) => ({
        category: item.valueTagName,
        value: item.completedCount ?? 0,
      }));

      const filtered = missionCreated.filter((t) => !t.valueTagName.includes("Invalid"));
      const metrics = filtered.map((t) => t.valueTagId.toString());
      const metricsWithName = filtered.map((t) => ({
        id: t.valueTagId?.toString() ?? "",
        name: t.valueTagName ?? "",
      }));
      const values = filtered.map((t) => t.totalPointsCreatedPercentage ?? 0);

      setAvgCreated({
        metricsWithName: metricsWithName,
        metrics: metrics,
        values: values,
      });

      setAvgEarned({
        metricsWithName: metricsWithName,
        metrics: metrics,
        values: values,
      });

      // 🔹 Instead of using API's percentage, hardcode your passRate
      const passRate = 100; // 
      setAvgPercentages({
        metrics: filtered.map((t) => t.valueTagName),
        values: filtered.map(() => passRate), // every metric gets the same passRate
      });

      // Set bar chart states
      setCreatedBarData(created);
      setEarnedBarData(earned);

      // ✅ (Optional) You can also store other parts for later use:
      setMissionCreators(res.data?.data?.missionCreatorLists ?? []);
      setUserOverview(res.data?.data?.userOverview ?? []);

    } catch (error) {
      console.error("Error fetching department points:", error);
      setCreatedBarData([]);
      setEarnedBarData([]);
      setAvgEarned({ metricsWithName: [], metrics: [], values: [] });
      setAvgCreated({ metricsWithName: [], metrics: [], values: [] });
      setMissionCreators([]);
      setUserOverview([]);
    }
  };

  const fetchActiveUser = async (departmentIds) => {
    try {
      const apiUrl = `${mobileApiUrl}dashboard/active-level-report`;
      const res = await api.post(apiUrl, {
        employeeId: Number(employeeId),
        departmentIds: [],
      });

      const activeLevels = res.data?.activeLevels ?? [];

      const dormantList = res.data?.dormantList ?? [];

      setDormantUser(dormantList);
      setActiveUserData(activeLevels);
    } catch (error) {
      setActiveUserData([]);
    }
  };

  useEffect(() => {
    if (!employeeId) return;

    const refreshData = async () => {
      try {
        await Promise.all([
          fetchStats(),
          fetchMostData(selectedDeptIds, startDate, endDate),
          fetchActiveUser(),
        ]);
      } catch (error) {
        console.error("Error refreshing data:", error);
      }
    };

    refreshData();
  }, [selectedDeptIds, startDate, endDate]);

  return (
    <Box sx={{ my: 3, }}>
      {/* Stats cards row */}
      <CardCounter cards={cards} />

      {/* Department selection */}
      <Department onChange={setSelectedDeptIds}
        startDate={startDate}
        endDate={endDate}
        onFilter={({ startDate, endDate }) => {
          setStartDate(startDate);
          setEndDate(endDate);
        }}
        />

      {/* Charts: Created */}
      <MissionCreatedBarchart createdBarData={createdBarData} avgCreated={avgCreated} avgPercentages={avgPercentages} />

      {/* Charts: Earned */}
      <MissionEarnedChart earnedBarData={earnedBarData} avgEarned={avgEarned} avgPercentages={avgPercentages} />

      {/* mission creator list */}
      <MissionCreatorChart missionCreators={missionCreators} />

      {/*  User Overview */}
      <UserOverviewChart userOverview={userOverview} />

      {/* User Activity Status Analysis */}
      <UserActicityStatusTable activeUserData={activeUserData} />

      {/* Dormant User Chart */}
      <DormantUserTable dormantUser={dormantUser} />

      {/* Daily Active Report Chart */}
      <DailyActivityReportLineChart
        activeList={activityList}
        changeFilter={updateFilter}
        filter={filter}
      />
    </Box>
  );

}
